import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Code, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Download, 
  Plus, 
  Trash2, 
  FileCode, 
  HelpCircle 
} from 'lucide-react';

export const SitemapGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [urls, setUrls] = useState([
    { loc: 'https://toolmetrico.com/', lastmod: '2026-05-14', changefreq: 'daily', priority: '1.0' },
    { loc: 'https://toolmetrico.com/image-compressor', lastmod: '2026-05-14', changefreq: 'weekly', priority: '0.8' },
    { loc: 'https://toolmetrico.com/qrcode-generator', lastmod: '2026-05-14', changefreq: 'weekly', priority: '0.8' },
    { loc: 'https://toolmetrico.com/about', lastmod: '2026-05-10', changefreq: 'monthly', priority: '0.5' }
  ]);
  const [copied, setCopied] = useState(false);

  const addUrl = () => {
    const today = new Date().toISOString().split('T')[0];
    setUrls([...urls, { loc: 'https://toolmetrico.com/new-page', lastmod: today, changefreq: 'weekly', priority: '0.7' }]);
  };

  const removeUrl = (index) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  const updateUrl = (index, field, value) => {
    const updated = [...urls];
    updated[index][field] = value;
    setUrls(updated);
  };

  // Generate XML string
  const generateXmlSitemap = () => {
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;
    
    urls.forEach(u => {
      xml += `  <url>\n`;
      xml += `    <loc>${u.loc}</loc>\n`;
      if (u.lastmod) xml += `    <lastmod>${u.lastmod}</lastmod>\n`;
      if (u.changefreq) xml += `    <changefreq>${u.changefreq}</changefreq>\n`;
      if (u.priority) xml += `    <priority>${u.priority}</priority>\n`;
      xml += `  </url>\n`;
    });

    xml += `</urlset>`;
    return xml;
  };

  const sitemapOutput = generateXmlSitemap();

  const handleCopy = () => {
    navigator.clipboard.writeText(sitemapOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([sitemapOutput], { type: 'text/xml' });
    element.href = URL.createObjectURL(file);
    element.download = "sitemap.xml";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleReset = () => {
    const today = new Date().toISOString().split('T')[0];
    setUrls([{ loc: 'https://toolmetrico.com/', lastmod: today, changefreq: 'daily', priority: '1.0' }]);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free XML Sitemap Generator - Visual Path Builder" 
        description="Generate valid XML sitemaps for Google Search Console. Configure change frequencies, priority scoring, and last modified dates instantly."
        keywords="sitemap generator, xml sitemap builder, create sitemap online, google search console sitemap, seo sitemap creator"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Sitemap Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          XML Sitemap Generator & Exporter
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Build fully valid XML roadmap structures to accelerate search engine indexing on Google Search Console and Bing Webmaster Tools.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Path Configurator</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RotateCcw size={14} /> Clear
              </button>
              <button onClick={addUrl} style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Plus size={14} /> Add URL
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {urls.map((u, idx) => (
              <div key={idx} style={{ background: 'var(--bg-secondary)', padding: '18px', borderRadius: '16px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Page Loc URL</span>
                    <input 
                      type="text" 
                      value={u.loc} 
                      onChange={(e) => updateUrl(idx, 'loc', e.target.value)}
                      placeholder="https://example.com/page" 
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  {urls.length > 1 && (
                    <button onClick={() => removeUrl(idx)} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', alignSelf: 'flex-end' }}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Last Mod</span>
                    <input 
                      type="date" 
                      value={u.lastmod} 
                      onChange={(e) => updateUrl(idx, 'lastmod', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Frequency</span>
                    <select 
                      value={u.changefreq} 
                      onChange={(e) => updateUrl(idx, 'changefreq', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                    >
                      <option value="always">Always</option>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                      <option value="never">Never</option>
                    </select>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Priority</span>
                    <select 
                      value={u.priority} 
                      onChange={(e) => updateUrl(idx, 'priority', e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                    >
                      <option value="1.0">1.0 (High Root)</option>
                      <option value="0.9">0.9</option>
                      <option value="0.8">0.8 (Standard Page)</option>
                      <option value="0.7">0.7</option>
                      <option value="0.6">0.6</option>
                      <option value="0.5">0.5 (Medium)</option>
                      <option value="0.3">0.3 (Low Archive)</option>
                      <option value="0.1">0.1</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Output XML */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileCode size={18} style={{ color: 'var(--primary)' }} /> Generated sitemap.xml
              </h3>

              <div style={{ display: 'flex', gap: '8px' }}>
                <AdToast message={toast} />
                <button 
                  onClick={withAd('sitemap-download', handleDownload)}
                  style={{ background: '#10b981', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
                >
                  <Download size={16} /> Download XML
                </button>
                <button 
                  onClick={withAd('sitemap-copy', handleCopy)}
                  style={{ background: copied ? '#10b981' : 'var(--primary)', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied Code!' : 'Copy Code'}
                </button>
              </div>
            </div>

            <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '20px', borderRadius: '14px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto', margin: 0, border: '1px solid rgba(255, 255, 255, 0.1)', lineHeight: 1.6 }}>
              {sitemapOutput}
            </pre>
          </div>

          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ color: 'var(--primary)', padding: '4px' }}>
              <HelpCircle size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Submission Guidance</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                Submit your generated XML sitemap URL directly into your <strong>Google Search Console</strong> dashboard under the &quot;Sitemaps&quot; section to ensure Googlebot crawls your latest updates instantly.
              </p>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="sitemap-generator" />
    </div>
  );
};

export default SitemapGenerator;
